import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../api/client';
import type { VerificationBill, VerificationComparison, BillingPocket, CreateBillRunRequest } from '../api/types';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { BadgeTone, DataTableColumn } from '../components/ui';
import { fmtCurrencyOrDash } from '../lib/fmt';

export default function BillVerification() {
  const [verificationBills, setVerificationBills] = useState<VerificationBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // New verification modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const [verificationBill, setVerificationBill] = useState<VerificationBill | null>(null);
  const [reviewStep, setReviewStep] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create run from verification
  const [pockets, setPockets] = useState<BillingPocket[]>([]);
  const [selectedPocketIds, setSelectedPocketIds] = useState<Set<string>>(new Set());
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [creating, setCreating] = useState(false);

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [comparisons, setComparisons] = useState<VerificationComparison[]>([]);
  const [expandedBill, setExpandedBill] = useState<VerificationBill | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const loadVerificationBills = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params: { status?: string } = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.getVerificationBills(params);
      setVerificationBills(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verification bills');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadVerificationBills();
  }, [loadVerificationBills]);

  const loadPockets = async () => {
    try {
      const res = await api.getPockets();
      setPockets(res.data);
    } catch {}
  };

  const handleFileUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB.');
      return;
    }
    setExtracting(true);
    setExtractionProgress('Uploading PDF...');
    setError(null);
    try {
      setExtractionProgress('Extracting bill data...');
      const res = await api.uploadVerificationBill(file);
      setVerificationBill(res.data);
      if (res.data.extraction_status === 'error') {
        setError(`Extraction failed: ${res.data.extraction_error || 'Unknown error'}`);
        setExtracting(false);
        return;
      }
      setExtractionProgress('Extraction complete');
      setReviewStep(true);

      if (res.data.account_number) {
        const match = pockets.find(p => p.account_number === res.data.account_number);
        if (match) setSelectedPocketIds(new Set([match.id]));
      }
      if (res.data.billing_period_start) setPeriodStart(res.data.billing_period_start);
      if (res.data.billing_period_end) setPeriodEnd(res.data.billing_period_end);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract bill data');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateVerificationRun = async () => {
    if (!verificationBill || selectedPocketIds.size === 0 || !periodStart || !periodEnd) {
      setError('Please select a pocket and confirm dates');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      await api.confirmVerificationBill(verificationBill.id, {});
      for (const pocketId of selectedPocketIds) {
        const data: CreateBillRunRequest = {
          period_start: periodStart,
          period_end: periodEnd,
          pocket_id: pocketId,
          verification_mode: true,
          verification_bill_id: verificationBill.id,
        };
        await api.createBillRunV2(data);
      }
      setShowNewModal(false);
      setVerificationBill(null);
      setReviewStep(false);
      setSelectedPocketIds(new Set());
      setPeriodStart('');
      setPeriodEnd('');
      await loadVerificationBills();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create verification run');
    } finally {
      setCreating(false);
    }
  };

  const handleExpandRow = async (vb: VerificationBill) => {
    if (expandedId === vb.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(vb.id);
    setLoadingDetail(true);
    try {
      const [billRes, compRes] = await Promise.all([
        api.getVerificationBill(vb.id),
        vb.verification_status === 'complete' ? api.getVerificationComparison(vb.id) : Promise.resolve({ data: [] }),
      ]);
      setExpandedBill(billRes.data);
      setComparisons(compRes.data);
    } catch {} finally {
      setLoadingDetail(false);
    }
  };

  const handleConfirmBill = async (vb: VerificationBill) => {
    try {
      setConfirming(true);
      setError(null);
      await api.confirmVerificationBill(vb.id, {});
      await loadVerificationBills();
      // Reload the expanded detail
      const res = await api.getVerificationBill(vb.id);
      setExpandedBill(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleRejectBill = async (vb: VerificationBill) => {
    const reason = prompt('Rejection reason (optional):');
    if (reason === null) return; // cancelled
    try {
      setRejecting(true);
      setError(null);
      await api.rejectVerificationBill(vb.id, reason || undefined);
      await loadVerificationBills();
      const res = await api.getVerificationBill(vb.id);
      setExpandedBill(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };

  // Filter + paginate verification bills
  const filteredVBills = verificationBills.filter(vb => {
    if (search) {
      const q = search.toLowerCase();
      if (!(vb.municipality_name || '').toLowerCase().includes(q) &&
          !(vb.account_number || '').toLowerCase().includes(q) &&
          !(vb.sender_email || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filteredVBills.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedVBills = filteredVBills.slice(startIdx, startIdx + pageSize);

  // Converged onto the shared formatter (src/lib/fmt.ts), which is where the
  // non-breaking separator after "R" lives — the hand-rolled ASCII space was
  // the only break opportunity in the string and orphaned the "R" onto its own
  // line in narrow table columns.
  const formatCurrency = fmtCurrencyOrDash;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' });
  };

  // Routed through ui/Badge. 'confirmed' (was indigo-100/800) maps to
  // 'success' — same extracting/extracted/confirmed/error progression as
  // BillRuns.tsx's STATUS_TONE (calculating/pending_approval/approved/error),
  // a hue normalization (indigo -> teal) consistent with that precedent, not
  // a new one. isRejected already used orange-100/800, an exact match for
  // Badge's 'orange' tone (shade-normalized 800->900).
  //
  // DELIBERATELY NOT src/lib/billRunStatusTone.ts, even though the wave that
  // converged BillRuns/BillRunDetail/Dashboard onto that module touched this
  // file. This map keys on `extraction_status` — a council-bill OCR
  // extraction lifecycle (extracting/extracted/confirmed/error), a different
  // vocabulary over a different entity from a bill run's
  // pending/calculating/pending_approval/approved/finalized. The two share
  // exactly one key name (`error`, and both already map it to 'danger'), and
  // merging them would mean a bill run could be given a tone for `extracted`
  // and an extraction a tone for `finalized`. The parallel *shape* is why the
  // hues were chosen to rhyme; it is not a reason to share storage.
  const STATUS_TONE: Record<string, BadgeTone> = {
    extracting: 'info',
    extracted: 'warning',
    confirmed: 'success',
    error: 'danger',
  };
  const getStatusBadge = (vb: VerificationBill) => {
    const status = vb.extraction_status;
    const isRejected = status === 'error' && vb.extraction_error?.startsWith('Rejected:');
    if (isRejected) return <Badge tone="orange">Rejected</Badge>;
    const labels: Record<string, string> = {
      extracting: 'Extracting',
      extracted: 'Pending Review',
      confirmed: 'Confirmed',
      error: 'Error',
    };
    return <Badge tone={STATUS_TONE[status]}>{labels[status]}</Badge>;
  };

  // pass/minor_variance/major_variance -> success/warning/danger, the same
  // verified/minor/major variance mapping used for the comparison-row status
  // chips below (and in BillRunDetail.tsx's identical comparison table).
  const RESULT_TONE: Record<string, BadgeTone> = {
    pass: 'success',
    minor_variance: 'warning',
    major_variance: 'danger',
  };
  const getResultBadge = (vb: VerificationBill) => {
    if (vb.verification_status !== 'complete' || !vb.verification_result) return null;
    const labels: Record<string, string> = {
      pass: 'Verified',
      minor_variance: 'Minor Variances',
      major_variance: 'Discrepancies',
    };
    return <Badge tone={RESULT_TONE[vb.verification_result]}>{labels[vb.verification_result]}</Badge>;
  };

  // No selection here, but there IS a rowActions toggle cell (see below) plus
  // DataTable's own expand toggle, so Municipality (identifying) + Total (the
  // number people scan for) is the ceiling — Account/Period/Status/Source/
  // Received/Confirmed are one tap away. DataTable has no whole-row-click
  // slot in its API, so the previous `<tr onClick>` (open the detail panel by
  // clicking anywhere in the row) becomes a dedicated chevron button in
  // rowActions instead — same detail panel, smaller tap target.
  const columns: DataTableColumn<VerificationBill>[] = [
    {
      key: 'municipality',
      label: 'Municipality',
      priority: 'primary',
      render: (vb) => <span className="font-medium text-content">{vb.municipality_name || '-'}</span>,
    },
    {
      key: 'account',
      label: 'Account',
      priority: 'secondary',
      render: (vb) => <span className="text-content-muted font-mono">{vb.account_number || '-'}</span>,
    },
    {
      key: 'period',
      label: 'Period',
      priority: 'secondary',
      render: (vb) =>
        vb.billing_period_start && vb.billing_period_end
          ? `${formatDate(vb.billing_period_start)} - ${formatDate(vb.billing_period_end)}`
          : '-',
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      render: (vb) => (
        <div className="flex items-center gap-2">
          {getStatusBadge(vb)}
          {getResultBadge(vb)}
        </div>
      ),
    },
    {
      key: 'total',
      label: 'Total',
      priority: 'primary',
      numeric: true,
      render: (vb) => <span className="font-medium">{formatCurrency(vb.total_amount_cents)}</span>,
    },
    {
      key: 'source',
      label: 'Source',
      priority: 'secondary',
      render: (vb) => (
        <Badge tone={vb.source === 'email' ? 'purple' : 'neutral'}>
          {vb.source === 'email' ? 'Email' : 'Web'}
        </Badge>
      ),
    },
    {
      key: 'received',
      label: 'Received',
      priority: 'secondary',
      render: (vb) => <span className="text-content-muted text-xs">{formatDateTime(vb.created_at)}</span>,
    },
    {
      key: 'confirmed',
      label: 'Confirmed',
      priority: 'secondary',
      render: (vb) => (
        <span className="text-content-muted text-xs">{vb.confirmed_at ? formatDateTime(vb.confirmed_at) : '-'}</span>
      ),
    },
  ];

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-content">Bill Verification</h1>
          <p className="text-sm text-content-muted mt-1">Upload council bills to verify against calculated charges</p>
        </div>
        <button
          onClick={() => { loadPockets(); setShowNewModal(true); setError(null); }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Verification
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search municipality, account, email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-72"
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">All Statuses</option>
          <option value="extracting">Extracting</option>
          <option value="extracted">Pending Review</option>
          <option value="confirmed">Confirmed</option>
          <option value="error">Error</option>
        </select>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          className="px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
        <span className="text-sm text-content-muted ml-auto">
          {filteredVBills.length} bill{filteredVBills.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error */}
      {error && !showNewModal && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card className="shadow-sm">
        {loading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-border-token rounded" />)}
            </div>
          </div>
        ) : paginatedVBills.length === 0 ? (
          <div className="px-6 py-12 text-center text-content-muted">
            <svg className="w-12 h-12 mx-auto mb-4 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="text-lg font-medium">No verification bills</p>
            <p className="mt-1">Upload a council bill to start verifying</p>
          </div>
        ) : (
          <div>
            <DataTable
              data={paginatedVBills}
              columns={columns}
              rowKey={(vb) => vb.id}
              density="comfortable"
              // A text label, not a chevron: DataTable renders its OWN expand
              // toggle in this table (2 primary + 6 secondary columns), and
              // that chevron opens a plain <dl> of the secondary columns —
              // a different, smaller disclosure than this button's rich
              // panel (meters, comparison, confirm/reject). Both used to be
              // an identical chevron icon (same path, same rotate, same
              // colours, differing only w-4 vs w-5) with no way for a
              // sighted user to tell them apart; a short text label reads
              // unambiguously at any size without needing a second icon
              // shape. aria-label/aria-expanded/tap sizing unchanged.
              //
              // Below lg this control now lives inside DataTable's own expand
              // detail, so opening this panel costs two taps. Reviewed and
              // KEPT, against the suggestion to promote the opener into the
              // Municipality primary cell:
              //
              //  1. The outer disclosure earns its tap. It is not a
              //     pass-through — six secondary columns hang off it
              //     (Account/Period/Status/Source/Received/Confirmed), and
              //     Status + Account are what most reads actually stop at.
              //     The second tap is only paid when a reader wants the
              //     meters/comparison panel as well.
              //  2. A primary cell is the wrong host for it. At 375px this
              //     table renders two primary columns in a `w-full` (not
              //     `min-w-max`) table, and `municipality_name` is unbounded
              //     free text. Putting a non-shrinking control beside
              //     wrapping free text in a width-constrained cell is exactly
              //     the min-content-floor overflow this branch has been
              //     fixing elsewhere — trading a tap for a clip.
              //  3. `render` is not width-aware. It runs at every width, so
              //     promoting the opener either duplicates it against the
              //     lg+ actions column or needs a new isDesktop conditional
              //     in this file — a behaviour change on the desktop layout
              //     that was already signed off.
              //
              // Collapsing the two into one disclosure is not available
              // either: the rich panel renders as a sibling of <DataTable>,
              // not per row, and it lazily fetches on open
              // (getVerificationBill + getVerificationComparison). Merging
              // would need a per-row detail slot in DataTable AND would fire
              // a request every time someone peeked at a row's secondary
              // columns.
              rowActions={(vb) => (
                <button
                  type="button"
                  onClick={() => handleExpandRow(vb)}
                  aria-expanded={expandedId === vb.id}
                  aria-label={expandedId === vb.id ? 'Hide verification details' : 'Show verification details'}
                  // `lg:min-h-0`: 44px is a TOUCH floor; in a table row it
                  // drives row height and costs rows-per-screen on desktop.
                  // Full rationale on Tenants.tsx's rowActions; escape
                  // documented at tailwind.config.js:103.
                  className="tap-target lg:min-h-0 px-1 text-accent hover:opacity-90"
                >
                  {expandedId === vb.id ? 'Hide' : 'Details'}
                </button>
              )}
            />

            {/* Expanded Detail */}
            {expandedId && (
              <div className="border-t border-border-token bg-surface-sunken p-6">
                {loadingDetail ? (
                  <div className="animate-pulse space-y-2">
                    {[1, 2, 3].map((i) => <div key={i} className="h-8 bg-border-token rounded" />)}
                  </div>
                ) : expandedBill && (
                  <div className="space-y-4">
                    {/* Meters */}
                    {expandedBill.meters && expandedBill.meters.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-content mb-2">Meters ({expandedBill.meters.length})</h4>
                        {/* 6 columns (Meter, Tariff, Prev, Curr, Units,
                            Amount) — the review's count of 4 was two low.
                            >=6, so `min-w-max`: meter numbers and tariff
                            codes are unbounded strings and the four numerics
                            are `toLocaleString`d, so at 375px these wrap into
                            slivers rather than triggering the scroller. See
                            DataTable.tsx:104-121. */}
                        <div className="overflow-x-auto">
                        <table className="w-full min-w-max text-xs">
                          <thead><tr className="text-content-muted">
                            <th className="text-left py-1 pr-2">Meter</th><th className="text-left py-1 pr-2">Tariff</th>
                            <th className="text-right py-1 pr-2">Prev</th><th className="text-right py-1 pr-2">Curr</th>
                            <th className="text-right py-1 pr-2">Units</th><th className="text-right py-1">Amount</th>
                          </tr></thead>
                          <tbody>
                            {expandedBill.meters.map((m) => (
                              <tr key={m.id} className="border-t border-border-token">
                                <td className="py-1 pr-2 font-medium">{m.meter_number || '-'}</td>
                                <td className="py-1 pr-2">{m.tariff_code || '-'}</td>
                                <td className="py-1 pr-2 text-right">{m.previous_reading?.toLocaleString() ?? '-'}</td>
                                <td className="py-1 pr-2 text-right">{m.current_reading?.toLocaleString() ?? '-'}</td>
                                <td className="py-1 pr-2 text-right">{m.units?.toLocaleString() ?? '-'}</td>
                                <td className="py-1 text-right font-medium">{m.amount_cents ? formatCurrency(m.amount_cents) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}

                    {/* Comparison Results */}
                    {comparisons.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-content mb-2">Verification Comparison</h4>
                        {/* 6 columns (Category, Council, Calculated, Diff, %,
                            Status) — the review's count of 7 was one high.
                            >=6, so `min-w-max`; same shape and same reasoning
                            as BillRunDetail.tsx's comparison table, and it
                            nests one level deeper (inside the expanded-row
                            panel), so it has even less width to give. See
                            DataTable.tsx:104-121. */}
                        <div className="overflow-x-auto">
                        <table className="w-full min-w-max text-sm">
                          {/* This sub-table nests inside the already-surface-sunken
                              expanded-row panel, so its own thead/total-row highlight
                              used one shade darker (gray-100) than the panel (gray-50)
                              to stay visually distinct. There's no second "sunken"
                              tier in the token set, so both collapse onto
                              surface-sunken here — a minor, deliberate flattening of
                              that two-tier nesting, not a missed site. */}
                          <thead className="bg-surface-sunken">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-content-muted uppercase">Category</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-content-muted uppercase">Council</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-content-muted uppercase">Calculated</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-content-muted uppercase">Diff</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-content-muted uppercase">%</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-content-muted uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparisons.map((comp) => (
                              <tr key={comp.id} className={comp.charge_category === 'total' ? 'bg-surface-sunken font-semibold' : 'border-t border-border-token'}>
                                <td className="px-3 py-2 capitalize">{comp.charge_category.replace(/_/g, ' ')}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatCurrency(comp.council_value_cents)}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatCurrency(comp.calculated_value_cents)}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatCurrency(comp.difference_cents)}</td>
                                <td className="px-3 py-2 text-right font-mono">{comp.difference_percent?.toFixed(1)}%</td>
                                <td className="px-3 py-2 text-center">
                                  <Badge tone={comp.status === 'match' ? 'success' : comp.status === 'minor_variance' ? 'warning' : 'danger'}>
                                    {comp.status === 'match' ? 'Match' : comp.status === 'minor_variance' ? 'Minor' : 'Major'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}

                    {/* Totals */}
                    <div className="bg-surface-raised rounded-lg p-3 flex justify-between text-sm">
                      <span className="text-content-muted">Electricity Total: <strong>{formatCurrency(expandedBill.electricity_total_cents)}</strong></span>
                      <span className="text-content-muted">VAT: <strong>{formatCurrency(expandedBill.vat_cents)}</strong></span>
                      <span className="font-bold">Total: {formatCurrency(expandedBill.total_amount_cents)}</span>
                    </div>

                    {expandedBill.sender_email && (
                      <p className="text-xs text-content-muted">Submitted via email by {expandedBill.sender_email}</p>
                    )}

                    {/* Actions */}
                    {expandedBill.extraction_status === 'extracted' && (
                      <div className="flex items-center gap-3 pt-3 border-t border-border-token">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleConfirmBill(expandedBill); }}
                          disabled={confirming}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium flex items-center gap-2"
                        >
                          {confirming && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          )}
                          Approve Extracted Values
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRejectBill(expandedBill); }}
                          disabled={rejecting}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium"
                        >
                          Reject
                        </button>
                        <span className="text-xs text-content-muted ml-auto">Approve to proceed with creating a verification run</span>
                      </div>
                    )}

                    {expandedBill.extraction_status === 'confirmed' && !expandedBill.bill_run_id && (
                      <div className="flex items-center gap-3 pt-3 border-t border-border-token">
                        <span className="text-sm text-indigo-700 dark:text-indigo-100 bg-indigo-50 dark:bg-indigo-900 px-3 py-1.5 rounded-lg">Values confirmed — create a verification run from "New Verification" to compare against calculated bills</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-content-muted">Showing {startIdx + 1}–{Math.min(startIdx + pageSize, filteredVBills.length)} of {filteredVBills.length}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">First</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
            <span className="px-3 py-1.5 text-sm text-content">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Last</button>
          </div>
        </div>
      )}

      {/* New Verification Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-surface-raised rounded-xl shadow-2xl w-full p-6 max-h-[90vh] overflow-y-auto ${reviewStep ? 'max-w-2xl' : 'max-w-md'}`}>
            <h2 className="text-xl font-bold text-content mb-6">New Bill Verification</h2>

            {!reviewStep ? (
              <div className="space-y-4">
                {extracting ? (
                  <Alert tone="info">
                    <div className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{extractionProgress}</span>
                    </div>
                  </Alert>
                ) : (
                  /* C5 (dark-mode contrast fix): hover:bg-indigo-50 had no
                     dark: counterpart (1.07:1 collapse). dark:hover:bg-indigo-900
                     — text-content 10.94:1 / text-content-muted 4.53:1 against
                     it (both clear AA; see src/test/contrast.test.ts).
                     indigo-950 was tried first and rejected: it's only
                     ~1.05:1 against this modal's surfaceRaised(dark)
                     background, the same "invisible against its card" failure
                     mode BillAuditPanel.tsx's comment documents for
                     amber/red/blue-950. indigo-900 stays distinct at 1.33:1.
                     hover:border-indigo-400 is left unprefixed — unchanged by
                     dark mode, it still reads at 5.09:1 against
                     surfaceRaised(dark) and 3.83:1 against the indigo-900
                     hover fill. */
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border-token rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900 transition-colors"
                  >
                    <svg className="w-10 h-10 text-content-muted mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm font-medium text-content">Upload council bill PDF</p>
                    <p className="text-xs text-content-muted mt-1">Eskom, Drakenstein, City of Cape Town, etc. (max 10MB)</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
                    />
                  </div>
                )}
              </div>
            ) : verificationBill && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-amber-700 dark:text-amber-100 bg-amber-50 dark:bg-amber-900 border border-amber-200 dark:border-amber-800 rounded p-2">
                    Please verify these values match the council bill. AI extraction can make mistakes.
                  </p>
                  {/* Final tidy (dark-mode contrast fix): text-indigo-600 on
                      this page's surface-raised(dark) card is 2.41:1,
                      dropping to 1.53:1 on hover (indigo-800) — both fail AA.
                      dark:text-indigo-400 clears the base at 5.09:1;
                      dark:hover:text-indigo-300 clears hover at 7.61:1
                      (same -600/-800 → dark:-400/dark:hover:-300 convention
                      as the red "Delete"/"Dismiss" links elsewhere, e.g.
                      Tenants.tsx / BuildingDetail.tsx). */}
                  <button onClick={() => { setReviewStep(false); setVerificationBill(null); }} className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 ml-2">Re-upload</button>
                </div>

                {/* Extracted info */}
                <div className="bg-surface-sunken rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-content-muted uppercase">Bill Info</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-content-muted">Municipality:</span> <strong>{verificationBill.municipality_name || '-'}</strong></div>
                    <div><span className="text-content-muted">Account:</span> <strong>{verificationBill.account_number || '-'}</strong></div>
                    <div><span className="text-content-muted">Invoice:</span> <strong>{verificationBill.invoice_number || '-'}</strong></div>
                    <div><span className="text-content-muted">Period:</span> <strong>{verificationBill.billing_period_start} to {verificationBill.billing_period_end}</strong></div>
                  </div>
                </div>

                {/* Meters */}
                {verificationBill.meters?.length > 0 && (
                  <div className="bg-surface-sunken rounded-lg p-3">
                    <p className="text-xs font-semibold text-content-muted uppercase mb-2">Meters ({verificationBill.meters.length})</p>
                    {/* 5 columns (Meter, Prev, Curr, Units, Amount) — the
                        review's count of 4 was one low. In the 3-5 judgment
                        band, and ruled NO `min-w-max`: every cell is short
                        bounded content (a meter number plus four numerics),
                        it renders at `text-xs`, and unlike the 6-column
                        sibling above it has no Tariff column and NO
                        horizontal cell padding at all (`py-1` only). ~343px
                        across five such columns fits without wrapping into
                        slivers, and forcing max-content here would make a
                        table that fits scroll for no reason — the regression
                        the >=6 rule is bounded to avoid. */}
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-content-muted">
                        <th className="text-left py-1">Meter</th><th className="text-right py-1">Prev</th>
                        <th className="text-right py-1">Curr</th><th className="text-right py-1">Units</th>
                        <th className="text-right py-1">Amount</th>
                      </tr></thead>
                      <tbody>
                        {verificationBill.meters.map((m) => (
                          <tr key={m.id} className="border-t border-border-token">
                            <td className="py-1 font-medium">{m.meter_number || '-'}</td>
                            <td className="py-1 text-right">{m.previous_reading?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 text-right">{m.current_reading?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 text-right">{m.units?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 text-right font-medium">{m.amount_cents ? formatCurrency(m.amount_cents) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* Line Items */}
                {verificationBill.line_items?.length > 0 && (
                  <div className="bg-surface-sunken rounded-lg p-3">
                    <p className="text-xs font-semibold text-content-muted uppercase mb-2">Charges ({verificationBill.line_items.length})</p>
                    {/* 4 columns (Description, Qty, Rate, Amount) — the
                        review's count of 3 was one low. In the 3-5 judgment
                        band, and ruled NO `min-w-max`, which is the opposite
                        call to BuildingDetail's 4-column table and turns on
                        WHICH kind of long content sits in the wide column.
                        Here it is a charge description: prose, with spaces,
                        so it wraps to two or three readable lines in the
                        space the three short numerics leave. `min-w-max`
                        would forbid that wrap and force the whole table to
                        the widest description's single-line width, making
                        every row scroll horizontally — strictly worse than
                        wrapped prose. Contrast BuildingDetail's Asset ID
                        column, where the long value is an unbreakable
                        identifier that cannot wrap usefully at all. */}
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-content-muted">
                        <th className="text-left py-1">Description</th><th className="text-right py-1">Qty</th>
                        <th className="text-right py-1">Rate</th><th className="text-right py-1">Amount</th>
                      </tr></thead>
                      <tbody>
                        {verificationBill.line_items.map((li) => (
                          <tr key={li.id} className="border-t border-border-token">
                            <td className="py-1">{li.description || li.charge_type}</td>
                            <td className="py-1 text-right">{li.quantity?.toLocaleString() ?? '-'}</td>
                            <td className="py-1 text-right">{li.rate_cents ? formatCurrency(li.rate_cents) : '-'}</td>
                            <td className="py-1 text-right font-medium">{formatCurrency(li.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* Totals */}
                <div className="bg-surface-sunken rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-content-muted">Electricity Total:</span>
                    <strong>{formatCurrency(verificationBill.electricity_total_cents)}</strong>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-content-muted">VAT (15%):</span>
                    <strong>{formatCurrency(verificationBill.vat_cents)}</strong>
                  </div>
                  <div className="flex justify-between text-sm font-bold border-t border-border-token pt-1 mt-1">
                    <span>Total:</span>
                    <span>{formatCurrency(verificationBill.total_amount_cents)}</span>
                  </div>
                </div>

                {/* Pocket selection + dates */}
                <div className="border-t border-border-token pt-4">
                  <p className="text-sm font-medium text-content mb-2">Create Verification Run</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-content-muted mb-1">Period Start</label>
                      <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-border-token rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-content-muted mb-1">Period End</label>
                      <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-border-token rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-content-muted mb-1">Billing Pocket</label>
                    <div className="max-h-32 overflow-y-auto border border-border-token rounded-lg">
                      {pockets.map((p) => (
                        // C4 (dark-mode contrast fix): bg-indigo-50 had no
                        // dark: counterpart (1.07:1 collapse). dark:bg-indigo-900
                        // — text-content 10.94:1 / text-content-muted 4.53:1
                        // against it (both clear AA; see
                        // src/test/contrast.test.ts). Same indigo-900 choice
                        // as C5/C6 for the identical reason: indigo-950 is
                        // ~1.05:1 against this modal's surfaceRaised(dark)
                        // background — functionally invisible — while
                        // indigo-900 stays visible at 1.33:1.
                        <label key={p.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${selectedPocketIds.has(p.id) ? 'bg-indigo-50 dark:bg-indigo-900' : 'hover:bg-surface-sunken'}`}>
                          <input type="checkbox" checked={selectedPocketIds.has(p.id)}
                            onChange={() => {
                              const next = new Set(selectedPocketIds);
                              next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                              setSelectedPocketIds(next);
                            }}
                            className="w-4 h-4 text-indigo-600 border-border-token rounded" />
                          <span>{p.name}</span>
                          {p.account_number && <span className="text-xs text-content-muted ml-auto">{p.account_number}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <Alert tone="danger" className="mt-4">
                {error}
              </Alert>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowNewModal(false); setVerificationBill(null); setReviewStep(false); setError(null); }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              {reviewStep && (
                <button
                  onClick={handleCreateVerificationRun}
                  disabled={creating || selectedPocketIds.size === 0 || !periodStart || !periodEnd}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {creating && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  Confirm & Create Verification Run
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
